import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import type { MeDTO, MeResponse } from '../../shared/dto/auth';
import { ApiFailure, api, onUnauthorized } from '../api/client';
import { useI18n } from '../i18n';
import { ensureSingleUser, wipeRevokedData } from '../offline/wipe';

export const ME_KEY = ['me'] as const;

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'offline-no-session' }
  | { status: 'signed-in'; me: MeDTO };

const Ctx = createContext<AuthState>({ status: 'loading' });

/**
 * The server ended this device's session without a logout here (admin deactivation, lost-iPad
 * revoke, "skrá út alls staðar" elsewhere, password reset, expiry): 401 'unauthorized' (requireUser).
 * 'invalid_credentials' — the only other 401 — answers a sign-in attempt and never means that.
 */
function isSessionGone(e: unknown): boolean {
  return e instanceof ApiFailure && e.status === 401 && e.code !== 'invalid_credentials';
}

async function fetchMe(): Promise<MeDTO | null> {
  try {
    return (await api<MeResponse>('/auth/me')).me;
  } catch (e) {
    if (e instanceof ApiFailure && e.status === 401) {
      // Also when no user was restored (e.g. an empty persisted cache): the last user's copies go.
      if (isSessionGone(e)) void wipeRevokedData();
      return null;
    }
    throw e;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { lang, setLang } = useI18n();
  const q = useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    meta: { persist: true },
    staleTime: 60_000,
    // Always re-validate the session on app start; a restored copy is only a fallback for offline use.
    refetchOnMount: 'always',
    retry: false,
  });

  // Any 401 signs out. When a user was signed in, the session was revoked on the server: every
  // copy of choir data leaves the device too (unsent own changes stay, see offline/wipe.ts). A 401
  // while signed out is a failed sign-in ('invalid_credentials'): nothing to remove.
  useEffect(
    () =>
      onUnauthorized((code?: unknown) => {
        const wasSignedIn = !!qc.getQueryData<MeDTO | null>(ME_KEY);
        qc.setQueryData(ME_KEY, null);
        if (wasSignedIn && code !== 'invalid_credentials') void wipeRevokedData();
      }),
    [qc],
  );

  const me = q.data ?? null;
  useEffect(() => {
    if (me && me.lang !== lang) setLang(me.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.lang]);

  useEffect(() => {
    if (me) void ensureSingleUser(me.id);
  }, [me?.id]);

  let state: AuthState;
  if (me) state = { status: 'signed-in', me };
  else if (q.data === null) state = { status: 'signed-out' };
  else if (q.isError) state = { status: 'offline-no-session' };
  else state = { status: 'loading' };

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  return useContext(Ctx);
}

/** The signed-in user, or null. */
export function useMe(): MeDTO | null {
  const s = useContext(Ctx);
  return s.status === 'signed-in' ? s.me : null;
}

/** For screens that only render when signed in. */
export function useRequiredMe(): MeDTO {
  const me = useMe();
  if (!me) throw new Error('not signed in');
  return me;
}
